import Vue from 'vue';
import 'isomorphic-unfetch';
import { ApolloClient } from 'apollo-client';
import { HttpLink } from 'apollo-link-http';
import { onError } from 'apollo-link-error';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloLink, Observable } from 'apollo-link';
import VueApollo from 'vue-apollo';
import store from '../store';
import router from '../router';
import { REFRESH_ACCESS_TOKEN_MUTATION } from '../constants/graphql/authentication';
import { isTebaMode } from '../utils/tebaMode';

const cache = new InMemoryCache();
export let apolloClient;

const httpLink = new HttpLink({
  uri: process.env.VUE_APP_API_ENDPOINT + '/graphql',
  fetchOptions: {
    mode: 'cors',
  },
});

const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  const accessToken = store.getters.accessToken;
  operation.setContext({
    headers: {
      Authorization: accessToken ? `${accessToken}` : null,
      // Tell the backend to log this request in the Teba way (industry number +
      // SMS) when the app is served from the Teba domain or debug_teba is on.
      ...(isTebaMode() ? { 'X-Omw-Auth-Mode': 'unique_identifier' } : {}),
    },
  });

  return forward(operation);
});

const versionCheckAfterware = new ApolloLink((operation, forward) => {
  return forward(operation).map(response => {
    const context = operation.getContext();
    const {
      response: { headers },
    } = context;

    if (headers) {
      const version = headers.get('Version');

      if (store.getters.appVersion) {
        if (
          version &&
          version != store.getters.appVersion &&
          !store.getters.newVersionAvailable
        ) {
          // update newVersionAvailable value if there is a version mismatch
          store.dispatch('updateNewVersionAvailable', true);
        }
      } else {
        // if version not set in vuex, then set version
        if (version) {
          store.dispatch('updateVersion', version);
        }
      }
    }

    return response;
  });
});

// Error Handling
const errorLink = onError(
  ({ response, graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        // if logged out use refresh token to get new access token and rerun query
        if (err.message == 'LOGGED_OUT') {
          return new Observable(observer => {
            const oldHeaders = operation.getContext().headers;
            const userId = store.getters.userIdFromToken;
            const refreshToken = store.getters.refreshToken;

            refreshAccessToken(refreshToken, userId)
              .then(refreshResponse => {
                const accessToken =
                  refreshResponse.data.refreshAccessToken.accessToken;
                const newRefreshToken =
                  refreshResponse.data.refreshAccessToken.refreshToken;

                store.dispatch('login', {
                  accessToken,
                  refreshToken: newRefreshToken,
                });

                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    Authorization: accessToken,
                  },
                });

                const subscriber = {
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                };

                // Retry last failed request
                forward(operation).subscribe(subscriber);
              })
              .catch(error => {
                // Refresh unsuccessful, we force user to login
                observer.error(error);
                clearPersistedApolloCache();
                store.dispatch('logout');
                router.push({ name: 'Login', query: { error: 2 } });
                response.errors = null;
              });
          });
        }

        // reload page if there is an error and a version mismatch
        const context = operation.getContext();
        const {
          response: { headers },
        } = context;

        if (headers) {
          const version = headers.get('Version');

          if (
            store.getters.appVersion &&
            version &&
            version != store.getters.appVersion
          ) {
            response.errors = null;
            window.location.search += '&reload=true';
          }
        }
      }
    }

    if (networkError) console.log(`[Network error]: ${networkError}`);
  }
);

function refreshAccessToken(refreshToken, userId) {
  return apolloClient.mutate({
    mutation: REFRESH_ACCESS_TOKEN_MUTATION,
    variables: {
      refreshToken,
      userId,
    },
  });
}

var link = authMiddleware.concat(httpLink);
link = versionCheckAfterware.concat(link);

Vue.use(VueApollo);

export async function clearPersistedApolloCache() {
  if (!apolloClient) return;
  try {
    await apolloClient.clearStore();
  } catch (error) {
    // Ignore clear failures in forced logout flows.
  }
}

export async function createApolloProvider() {
  apolloClient = new ApolloClient({
    fetchOptions: { fetch },
    link: errorLink.concat(link),
    cache,
    connectToDevTools: true,
    fetchOptions: {
      mode: 'no-cors',
    },
  });

  return new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {
      $loadingKey: 'loading',
    },
  });
}
